import { Observable, concat, defer, from, of } from 'rxjs';
import { catchError, concatMap, map, takeWhile, throwIfEmpty } from 'rxjs/operators';

/**
 * The execution order of a split, as a stream of events. Pure rxjs -- the
 * dialog supplies how a payment is created and how the bank row is recorded.
 *
 * Fixed rules (owner, 2026-09-29; `docs/PROMPT_agent-bank-payment-review-multi-invoice.md`):
 *  1. Lines run one at a time, in the given order.
 *  2. The bank row is recorded as processed right after the FIRST payment
 *     that saves, before any further payment is created. (If a later payment
 *     fails the cost is a visibly still-open invoice; the alternative, recording
 *     only after all succeed, risks a reload leading to a duplicate payment.)
 *  3. The sequence stops at the first failure of either kind. Nothing is retried
 *     automatically and nothing is rolled back or deleted.
 *
 * Failures are emitted as events, not thrown, so the caller sees exactly which
 * line stopped the run; the stream always completes normally.
 */
export type SplitEvent =
  | { type: 'created'; index: number; paymentId: number }
  | { type: 'recorded'; index: number }
  /** The payment for this line was not (fully) created. `error` says how. */
  | { type: 'failed'; index: number; error: unknown }
  /** The payment WAS created but recording the bank row failed. */
  | { type: 'recordFailed'; index: number; paymentId: number; error: unknown };

export interface SplitDeps<T> {
  /** Creates the payment for one line; emits its DOCID once. */
  create(line: T): Observable<number>;
  /** Records the bank row as processed against `paymentId` and this line's invoice. */
  record(line: T, paymentId: number): Observable<unknown>;
}

/**
 * @param alreadyRecorded - true when the bank row was already recorded in an
 *   earlier run (a "retry the rest" after the first payment succeeded), so
 *   `record` is never called again.
 */
export function runSplitSequence<T>(lines: T[], deps: SplitDeps<T>, alreadyRecorded: boolean): Observable<SplitEvent> {
  return defer(() => {
    let recorded = alreadyRecorded;

    const runLine = (line: T, index: number): Observable<SplitEvent> =>
      defer(() => deps.create(line)).pipe(
        throwIfEmpty(() => new Error('Payment creation emitted no document id.')),
        concatMap((paymentId): Observable<SplitEvent> => {
          const created: SplitEvent = { type: 'created', index, paymentId };
          if (recorded) {
            return of(created);
          }
          return concat(
            of(created),
            defer(() => deps.record(line, paymentId)).pipe(
              map((): SplitEvent => {
                recorded = true;
                return { type: 'recorded', index };
              }),
              catchError((error): Observable<SplitEvent> => of({ type: 'recordFailed', index, paymentId, error }))
            )
          );
        }),
        catchError((error): Observable<SplitEvent> => of({ type: 'failed', index, error }))
      );

    return from(lines).pipe(
      concatMap((line, index) => runLine(line, index)),
      // Inclusive: the failure event itself is delivered, then nothing further starts.
      takeWhile(event => event.type !== 'failed' && event.type !== 'recordFailed', true)
    );
  });
}
